<template>
  <div class="scan-stock-query-container">
    <!-- 头部导航 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">扫码盘点</h1>
      <button @click="handleAddCheck" class="btn add-btn">新增盘点</button>
    </header>

    <!-- 主内容区 -->
    <main class="content">
      <div class="form-section source-section">
        <h3 class="section-title">盘点订单</h3>

        <!-- 状态提示横幅 -->
        <div v-if="statusMsg" class="status-banner" :class="statusType">
          {{ statusMsg }}
        </div>

        <!-- 加载中状态 -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>
        
        <!-- 错误提示 -->
        <div v-if="errorMsg" class="error-message">{{ errorMsg }}</div>

        <!-- 空状态 -->
        <div v-if="checkList.length === 0 && !loading && !errorMsg" class="empty-state">
          <div class="empty-icon">📄</div>
          <p class="empty-text">暂无盘点订单</p>
          <button @click="handleAddCheck" class="btn empty-action-btn">立即创建</button>
        </div>

        <!-- 盘点列表 -->
        <div class="card-list" v-else-if="!loading && !errorMsg">
          <div class="data-card" v-for="item in checkList" :key="item.id" @click="navigateToDetail(item.id)">
            <div class="card-header">
              <span class="card-label">盘点单号 {{ item.inventeNo }}</span>
              <span class="card-status" :class="getStatusClass(item.status)">{{ getStatusText(item.status) }}</span>
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="row-label">创建时间：</span>
                <span class="row-value">{{ formatDate(item.createTime) }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">详情单数量：</span>
                <span class="row-value">{{ item.stockCheckDetailList?.length || 0 }} 个</span>
              </div>
              <div class="card-row">
                <span class="row-label">审核状态：</span>
                <span class="row-value">{{ getCheckStatusText(item.checkStatus) }}</span>
              </div>
            </div>
            <div class="card-footer">
              <button 
                @click="handleEnterCheck(item.id, $event)" 
                class="btn enter-check-btn"
                :disabled="item.status === 2 || item.status === 3"
              >
                {{ item.status === 0 ? '开始盘点' : item.status === 1 ? '继续盘点' : '已完成' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 新增盘点弹窗 -->
    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">新增盘点单</h3>
          <button @click="resetModal" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <!-- 盘点方式选择 -->
          <div class="form-group">
            <label>盘点方式</label>
            <div class="method-selection">
              <button 
                class="method-btn" 
                :class="{ 'selected': checkMethod === 'supplier' }"
                @click="checkMethod = 'supplier'"
              >
                按供应商
              </button>
              <button 
                class="method-btn" 
                :class="{ 'selected': checkMethod === 'material' }"
                @click="checkMethod = 'material'"
              >
                按物料编码
              </button>
            </div>
          </div>

          <!-- 按供应商盘点 -->
          <div v-if="checkMethod === 'supplier'" class="form-group">
            <label>选择供应商</label>
            <!-- 多选列表 -->
            <div class="supplier-list">
              <div 
                v-for="supplier in suppliers" 
                :key="supplier.id"
                class="supplier-item" 
                :class="{ 'selected': selectedSuppliers.includes(supplier.id) }"
                @click="toggleSupplierSelection(supplier.id)"
              >
                <div class="supplier-info">
                  <div class="supplier-name">{{ supplier.supplierName }}</div>
                  <div class="supplier-code">{{ supplier.supplierCode }}</div>
                </div>
                <div class="checkbox" :class="{ 'checked': selectedSuppliers.includes(supplier.id) }"></div>
              </div>
            </div>
            <p class="form-hint">点击选择供应商，可多选</p>
          </div>

          <!-- 按物料编码盘点 -->
          <div v-else-if="checkMethod === 'material'" class="form-group">
            <label>物料编码</label>
            <input 
              type="text" 
              v-model="materialNo"
              placeholder="请输入物料编码"
              class="material-input"
              @keyup.enter="createCheckOrder"
            />
            <p class="form-hint">输入物料编码后创建盘点单</p>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="resetModal" class="btn modal-cancel">取消</button>
          <button @click="createCheckOrder" class="btn modal-confirm" :disabled="loading">
            {{ loading ? '创建中...' : '创建盘点单' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getStockCheckHistory, getSuppliers, copyStockToStockCheck, getId, createCheckByMaterialNo } from '@/api/stockCheck'

export default {
  name: 'ScanStockCheck',
  setup() {
    const router = useRouter()
    
    // 状态管理
    const checkList = ref([])
    const loading = ref(false)
    const errorMsg = ref('')
    const statusMsg = ref('')
    const statusType = ref('success')
    const showAddModal = ref(false)
    const suppliers = ref([])
    const selectedSuppliers = ref([])
    const checkRemark = ref('')
    // 新增：盘点方式和物料编码
    const checkMethod = ref('supplier') // 默认按供应商盘点
    const materialNo = ref('')

    // 获取盘点历史
    const fetchCheckHistory = async () => {
      try {
        loading.value = true
        errorMsg.value = ''
        const res = await getStockCheckHistory({ current: 1, size: 20 })
        checkList.value = res.data.records || []
        
        if (checkList.value.length === 0) {
          statusMsg.value = '暂无盘点记录，点击"新增盘点"开始创建'
          statusType.value = 'info'
        } else {
          statusMsg.value = ''
        }
      } catch (err) {
        errorMsg.value = '获取盘点历史失败，请稍后重试'
        console.error(err)
      } finally {
        loading.value = false
      }
    }

    // 获取供应商列表
    const fetchSuppliers = async () => {
      try {
        const res = await getSuppliers({ current: 1, size: 100 })
        suppliers.value = res.data.records || []
      } catch (err) {
        console.error('获取供应商失败', err)
        setStatus('获取供应商列表失败', 'error')
      }
    }

    // 创建盘点单
    const createCheckOrder = async () => {
      try {
        loading.value = true
        
        // 根据盘点方式选择不同的API
        if (checkMethod.value === 'supplier') {
          // 按供应商盘点
          if (selectedSuppliers.value.length === 0) {
            setStatus('请至少选择一个供应商', 'error')
            loading.value = false
            return
          }
          const res = await copyStockToStockCheck(selectedSuppliers.value)
          if (res.success) {
            setStatus('盘点单创建成功', 'success')
          } else {
            setStatus(res.msg || '创建盘点单失败', 'error')
          }
        } else if (checkMethod.value === 'material') {
          // 按物料编码盘点
          if (!materialNo.value.trim()) {
            setStatus('请输入物料编码', 'error')
            loading.value = false
            return
          }
          const res = await createCheckByMaterialNo(materialNo.value.trim())
          if (res.success) {
            setStatus('盘点单创建成功', 'success')
          } else {
            setStatus(res.msg || '创建盘点单失败', 'error')
          }
        }
        
        // 如果创建成功，关闭弹窗并重置表单
        if (statusType.value === 'success') {
          resetModal()
          fetchCheckHistory() // 刷新列表
        }
      } catch (err) {
        setStatus('创建盘点单失败，请稍后重试', 'error')
        console.error(err)
      } finally {
        loading.value = false
      }
    }

    // 重置弹窗状态
    const resetModal = () => {
      showAddModal.value = false
      selectedSuppliers.value = []
      materialNo.value = ''
      checkMethod.value = 'supplier' // 默认回到按供应商盘点
    }

    // 设置状态提示
    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      setTimeout(() => {
        if (statusMsg.value === message) statusMsg.value = ''
      }, 3000)
    }

    // 处理进入盘点
    const handleEnterCheck = async (id, event) => {
      // 阻止事件冒泡，避免同时触发卡片点击
      event.stopPropagation()
      try {
        loading.value = true
        // 调用getId接口获取盘点详情
        const res = await getId(id)
        if (res.success) {
          // 跳转到盘点详情页，可携带数据
          router.push({
            path: `/scan-stock-check/detail/${id}`,
            query: { checkDetail: JSON.stringify(res.data) }
          })
        } else {
          setStatus(res.msg || '获取盘点详情失败', 'error')
        }
      } catch (err) {
        console.error('获取盘点详情失败', err)
        setStatus('获取盘点详情失败，请稍后重试', 'error')
      } finally {
        loading.value = false
      }
    }

    // 获取状态文本
    const getStatusText = (status) => {
      const statusMap = {
        0: '待盘点',
        1: '盘点中',
        2: '已完成',
        3: '已取消'
      }
      return statusMap[status] || '未知状态'
    }

    // 获取审核状态文本
    const getCheckStatusText = (checkStatus) => {
      const checkStatusMap = {
        0: '待审核',
        1: '审核通过',
        2: '审核不通过'
      }
      return checkStatusMap[checkStatus] || '未知审核状态'
    }

    // 页面初始化
    onMounted(() => {
      fetchCheckHistory()
      fetchSuppliers()
    })

    // 供应商选择切换
    const toggleSupplierSelection = (supplierId) => {
      if (selectedSuppliers.value.includes(supplierId)) {
        // 如果已选中则移除
        selectedSuppliers.value = selectedSuppliers.value.filter(id => id !== supplierId);
      } else {
        // 如果未选中则添加
        selectedSuppliers.value.push(supplierId);
      }
    };

    return {
      checkList,
      loading,
      errorMsg,
      statusMsg,
      statusType,
      showAddModal,
      suppliers,
      selectedSuppliers,
      checkRemark,
      checkMethod,
      materialNo,
      
      // 方法
      handleBack: () => router.go(-1),
      handleAddCheck: () => showAddModal.value = true,
      navigateToDetail: (id) => router.push(`/scan-stock-check/detail/${id}`),
      createCheckOrder,
      resetModal,
      formatDate: (date) => {
        if (!date) return ''
        return new Date(date).toLocaleString()
      },
      getStatusClass: (status) => `status-${status}`,
      getStatusText,
      getCheckStatusText,
      handleEnterCheck,
      toggleSupplierSelection
    }
  }
}
</script>


<style scoped>

/* 基础容器样式 */
.scan-stock-query-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-primary);
  font-size: 14px;
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 3px 16px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.icon-back {
  width: 24px;
  height: 24px;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

/* 新增盘点按钮样式 */
.add-btn {
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  min-height: 40px;
  margin-left: auto;
  transition: background-color 0.2s;
}

.add-btn:hover {
  background-color: var(--primary-hover);
}

/* 内容区域样式 */
.content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  box-sizing: border-box;
  padding-bottom: 20px;
}

/* 表单区域样式 */
.form-section {
  background: var(--card-background);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.section-title {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: var(--primary-color);
  margin-right: 8px;
  border-radius: 2px;
}

/* 状态提示样式 */
.status-banner {
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  transition: opacity 0.3s;
}

.status-banner.success {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-banner.warn {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.status-banner.info {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  border: 1px solid rgba(35, 177, 192, 0.3);
}

/* 加载中状态 */
.loading-state {
  padding: 48px 0;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(35, 177, 192, 0.2);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 14px;
}

/* 错误提示文本 */
.error-message {
  color: #ef4444;
  font-size: 14px;
  margin: 0 0 8px 0;
  text-align: center;
  padding: 12px;
}

/* 空状态样式 */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: var(--text-secondary);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 24px;
}

.empty-action-btn {
  background-color: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.empty-action-btn:hover {
  background-color: #16a3b8;
}

/* 盘点列表卡片样式 */
.card-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.data-card {
  background-color: var(--card-background);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
}

.data-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.card-header {
  background-color: rgba(35, 177, 192, 0.05);
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-label {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

/* 状态标签样式 */
.status-0 { /* 待盘点 */
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.status-1 { /* 盘点中 */
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.status-2 { /* 已完成 */
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.status-3 { /* 已取消 */
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.card-body {
  padding: 16px;
}

.card-row {
  display: flex;
  margin-bottom: 12px;
  line-height: 1.5;
}

.card-row:last-child {
  margin-bottom: 0;
}

.row-label {
  color: var(--text-secondary);
  width: 100px;
  flex-shrink: 0;
}

.row-value {
  color: var(--text-primary);
  flex: 1;
}

.card-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
}

.enter-check-btn {
  background-color: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.enter-check-btn:hover:not(:disabled) {
  background-color: #16a3b8;
}

.enter-check-btn:disabled {
  background-color: rgba(35, 177, 192, 0.5);
  cursor: not-allowed;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(2px);
}

.modal-container {
  width: 100%;
  max-width: 500px;
  background-color: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: modalFadeIn 0.3s;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.modal-close:hover {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--text-primary);
}

.modal-body {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
}

.modal-footer .btn {
  flex: 1;
  min-height: 44px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: background-color 0.2s;
}

.modal-cancel {
  background-color: rgba(35, 177, 192, 0.05);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.modal-cancel:hover {
  background-color: rgba(35, 177, 192, 0.12);
}

.modal-confirm {
  background-color: var(--primary-color);
  color: #fff;
}

.modal-confirm:disabled {
  background-color: rgba(35, 177, 192, 0.5);
  cursor: not-allowed;
}

.modal-confirm:not(:disabled):hover {
  background-color: #16a3b8;
}

/* 表单控件样式 */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 44px;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.form-control:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
}

textarea.form-control {
  resize: vertical;
}

.form-hint {
  margin: 6px 0 0 0;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

/* 供应商列表样式 */
.supplier-list {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
}

.supplier-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.supplier-item:hover {
  background-color: rgba(35, 177, 192, 0.12);
}

.supplier-item.selected {
  background-color: rgba(35, 177, 192, 0.12);
  border: 1px solid var(--primary-color);
}

.supplier-info {
  flex: 1;
}

.supplier-name {
  font-weight: 500;
  color: var(--text-primary);
}

.supplier-code {
  font-size: 12px;
  color: var(--text-secondary);
}

/* 自定义复选框样式 */
.checkbox {
  width: 18px;
  height: 18px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  position: relative;
}

.checkbox.checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.checkbox.checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 14px;
  top: -2px;
  left: 3px;
}

/* 盘点方式选择样式 */
.method-selection {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.method-btn {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid var(--border-color);
  background-color: var(--card-background);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.method-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.method-btn.selected {
  border-color: var(--primary-color);
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

/* 物料编码输入框样式 */
.material-input {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.3s;
  margin-top: 8px;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.material-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
}

.material-input::placeholder {
  color: var(--text-secondary);
}

/* 加载动画 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 响应式适配 */
@media (max-width: 480px) {
  .header {
    padding: 3px 8px;
  }
  
  .add-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .card-list {
    gap: 8px;
  }
  
  .card-header, .card-body, .card-footer {
    padding: 10px 12px;
  }
  
  .card-label {
    font-size: 14px;
  }
  
  .modal-body {
    padding: 12px;
  }
  
  .modal-footer {
    padding: 8px 12px;
  }
  
  .modal-footer .btn {
    padding: 8px 12px;
    font-size: 14px;
  }
}
</style>